import type { BlockState } from "./blockSpec.ts";
import type { VoxelWorld } from "./VoxelWorld.ts";

export interface WalkVector {
  x: number;
  y: number;
  z: number;
}

export interface WalkCollisionBox {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

export interface WalkPhysicsOptions {
  radius: number;
  height: number;
  eyeHeight: number;
  groundLevel: number;
  gravity: number;
  jumpSpeed: number;
  walkSpeed: number;
  sprintSpeed: number;
}

export interface WalkMotionResult {
  position: WalkVector;
  grounded: boolean;
  collidedX: boolean;
  collidedY: boolean;
  collidedZ: boolean;
}

export const DEFAULT_WALK_PHYSICS: Readonly<WalkPhysicsOptions> = {
  radius: 0.3,
  height: 1.8,
  eyeHeight: 1.62,
  groundLevel: -0.5,
  gravity: 27.5,
  jumpSpeed: 8.2,
  walkSpeed: 4.35,
  sprintSpeed: 6.35
};

const COLLISION_EPSILON = 0.0001;

const PASSABLE_BLOCK_IDS = new Set([
  "air",
  "cave_air",
  "void_air",
  "water",
  "lava",
  "short_grass",
  "tall_grass",
  "fern",
  "large_fern",
  "dead_bush",
  "vine",
  "glow_lichen",
  "hanging_roots",
  "redstone_wire",
  "tripwire",
  "fire",
  "soul_fire"
]);

function fullBlockBox(
  x: number,
  y: number,
  z: number,
  extraHeight = 0
): WalkCollisionBox {
  return {
    minX: x - 0.5,
    minY: y - 0.5,
    minZ: z - 0.5,
    maxX: x + 0.5,
    maxY: y + 0.5 + extraHeight,
    maxZ: z + 0.5
  };
}

function shortenedBlockBox(
  x: number,
  y: number,
  z: number,
  height: number
): WalkCollisionBox {
  return {
    minX: x - 0.5,
    minY: y - 0.5,
    minZ: z - 0.5,
    maxX: x + 0.5,
    maxY: y - 0.5 + height,
    maxZ: z + 0.5
  };
}

function isDecorationWithoutCollision(blockId: string): boolean {
  return (
    blockId.endsWith("_flower") ||
    blockId.endsWith("_sapling") ||
    blockId.endsWith("_torch") ||
    blockId.endsWith("_wall_torch") ||
    blockId.endsWith("_button") ||
    blockId.endsWith("_pressure_plate") ||
    blockId.endsWith("_sign") ||
    blockId.endsWith("_wall_sign") ||
    blockId.endsWith("_hanging_sign") ||
    blockId.endsWith("_banner") ||
    blockId.endsWith("_wall_banner") ||
    blockId.endsWith("_coral") ||
    blockId.endsWith("_coral_fan") ||
    blockId.endsWith("_wall_coral_fan") ||
    blockId.endsWith("_mushroom") ||
    blockId === "cobweb"
  );
}

export function getWalkCollisionBoxesForBlock(
  blockId: string,
  state: BlockState,
  x: number,
  y: number,
  z: number
): WalkCollisionBox[] {
  if (
    PASSABLE_BLOCK_IDS.has(blockId) ||
    isDecorationWithoutCollision(blockId)
  ) {
    return [];
  }

  if (
    state.open === "true" &&
    (
      blockId.endsWith("_door") ||
      blockId.endsWith("_trapdoor") ||
      blockId.endsWith("_fence_gate")
    )
  ) {
    return [];
  }

  if (blockId.endsWith("_slab")) {
    const slabType = state.type || "bottom";
    if (slabType === "double") {
      return [fullBlockBox(x, y, z)];
    }
    if (slabType === "top") {
      return [{
        minX: x - 0.5,
        minY: y,
        minZ: z - 0.5,
        maxX: x + 0.5,
        maxY: y + 0.5,
        maxZ: z + 0.5
      }];
    }
    return [shortenedBlockBox(x, y, z, 0.5)];
  }

  if (blockId === "snow") {
    const layers = Math.max(
      1,
      Math.min(8, Number.parseInt(state.layers || "1", 10) || 1)
    );
    return [
      shortenedBlockBox(x, y, z, layers / 8)
    ];
  }

  if (blockId.endsWith("_carpet") || blockId === "moss_carpet") {
    return [shortenedBlockBox(x, y, z, 1 / 16)];
  }

  if (blockId.endsWith("_bed")) {
    return [shortenedBlockBox(x, y, z, 9 / 16)];
  }

  if (
    blockId.includes("chest") ||
    blockId.endsWith("_shulker_box")
  ) {
    return [shortenedBlockBox(x, y, z, 14 / 16)];
  }

  if (
    blockId.endsWith("_fence") ||
    blockId.endsWith("_wall") ||
    blockId.endsWith("_fence_gate")
  ) {
    return [fullBlockBox(x, y, z, 0.5)];
  }

  if (
    blockId === "farmland" ||
    blockId === "dirt_path"
  ) {
    return [shortenedBlockBox(x, y, z, 15 / 16)];
  }

  if (blockId === "enchanting_table") {
    return [shortenedBlockBox(x, y, z, 12 / 16)];
  }

  return [fullBlockBox(x, y, z)];
}

function createPlayerBox(
  position: WalkVector,
  options: WalkPhysicsOptions
): WalkCollisionBox {
  return {
    minX: position.x - options.radius,
    minY: position.y,
    minZ: position.z - options.radius,
    maxX: position.x + options.radius,
    maxY: position.y + options.height,
    maxZ: position.z + options.radius
  };
}

function boxesOverlap(
  first: WalkCollisionBox,
  second: WalkCollisionBox
): boolean {
  return (
    first.maxX > second.minX + COLLISION_EPSILON &&
    first.minX < second.maxX - COLLISION_EPSILON &&
    first.maxY > second.minY + COLLISION_EPSILON &&
    first.minY < second.maxY - COLLISION_EPSILON &&
    first.maxZ > second.minZ + COLLISION_EPSILON &&
    first.minZ < second.maxZ - COLLISION_EPSILON
  );
}

function collectNearbyCollisionBoxes(
  world: VoxelWorld,
  playerBox: WalkCollisionBox
): WalkCollisionBox[] {
  const boxes: WalkCollisionBox[] = [];
  const minX = Math.floor(playerBox.minX - 1);
  const maxX = Math.ceil(playerBox.maxX + 1);
  const minY = Math.floor(playerBox.minY - 1);
  const maxY = Math.ceil(playerBox.maxY + 1);
  const minZ = Math.floor(playerBox.minZ - 1);
  const maxZ = Math.ceil(playerBox.maxZ + 1);

  for (let y = minY; y <= maxY; y += 1) {
    for (let z = minZ; z <= maxZ; z += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        const block = world.getBlockData(x, y, z);
        if (!block) continue;
        const blockBoxes = getWalkCollisionBoxesForBlock(
          block.blockId,
          block.state,
          x,
          y,
          z
        );
        boxes.push(...blockBoxes);
      }
    }
  }

  return boxes;
}

function resolveHorizontalAxis(
  world: VoxelWorld,
  current: WalkVector,
  proposed: WalkVector,
  axis: "x" | "z",
  delta: number,
  options: WalkPhysicsOptions
): { position: WalkVector; collided: boolean } {
  if (Math.abs(delta) < COLLISION_EPSILON) {
    return { position: proposed, collided: false };
  }

  const playerBox = createPlayerBox(proposed, options);
  const collisions = collectNearbyCollisionBoxes(
    world,
    playerBox
  ).filter((box) => boxesOverlap(playerBox, box));

  if (collisions.length === 0) {
    return { position: proposed, collided: false };
  }

  let resolvedCoordinate = proposed[axis];

  collisions.forEach((box) => {
    if (axis === "x") {
      if (delta > 0) {
        resolvedCoordinate = Math.min(
          resolvedCoordinate,
          box.minX - options.radius - COLLISION_EPSILON
        );
      } else {
        resolvedCoordinate = Math.max(
          resolvedCoordinate,
          box.maxX + options.radius + COLLISION_EPSILON
        );
      }
    } else if (delta > 0) {
      resolvedCoordinate = Math.min(
        resolvedCoordinate,
        box.minZ - options.radius - COLLISION_EPSILON
      );
    } else {
      resolvedCoordinate = Math.max(
        resolvedCoordinate,
        box.maxZ + options.radius + COLLISION_EPSILON
      );
    }
  });

  return {
    position: {
      ...proposed,
      [axis]: resolvedCoordinate
    },
    collided: true
  };
}

export function resolveWalkMotion(
  world: VoxelWorld,
  position: WalkVector,
  movement: WalkVector,
  options: WalkPhysicsOptions = DEFAULT_WALK_PHYSICS
): WalkMotionResult {
  let next = { ...position };
  let collidedX = false;
  let collidedY = false;
  let collidedZ = false;
  let grounded = false;

  const xProposal = {
    ...next,
    x: next.x + movement.x
  };
  const xResult = resolveHorizontalAxis(
    world,
    next,
    xProposal,
    "x",
    movement.x,
    options
  );
  next = xResult.position;
  collidedX = xResult.collided;

  const zProposal = {
    ...next,
    z: next.z + movement.z
  };
  const zResult = resolveHorizontalAxis(
    world,
    next,
    zProposal,
    "z",
    movement.z,
    options
  );
  next = zResult.position;
  collidedZ = zResult.collided;

  const previousY = next.y;
  const yProposal = {
    ...next,
    y: next.y + movement.y
  };
  let resolvedY = yProposal.y;
  const playerBox = createPlayerBox(yProposal, options);
  const collisions = collectNearbyCollisionBoxes(
    world,
    playerBox
  ).filter((box) => boxesOverlap(playerBox, box));

  if (movement.y < 0) {
    collisions.forEach((box) => {
      if (previousY >= box.maxY - COLLISION_EPSILON) {
        resolvedY = Math.max(
          resolvedY,
          box.maxY + COLLISION_EPSILON
        );
        collidedY = true;
        grounded = true;
      }
    });
  } else if (movement.y > 0) {
    const previousTop = previousY + options.height;
    collisions.forEach((box) => {
      if (previousTop <= box.minY + COLLISION_EPSILON) {
        resolvedY = Math.min(
          resolvedY,
          box.minY - options.height - COLLISION_EPSILON
        );
        collidedY = true;
      }
    });
  }

  if (resolvedY < options.groundLevel) {
    resolvedY = options.groundLevel;
    collidedY = true;
    grounded = true;
  }

  next.y = resolvedY;

  if (!grounded && movement.y <= 0) {
    const probe = {
      ...next,
      y: next.y - 0.003
    };
    const probeBox = createPlayerBox(probe, options);
    const supported = collectNearbyCollisionBoxes(
      world,
      probeBox
    ).some((box) => boxesOverlap(probeBox, box));
    grounded =
      supported ||
      next.y <= options.groundLevel + COLLISION_EPSILON;
  }

  return {
    position: next,
    grounded,
    collidedX,
    collidedY,
    collidedZ
  };
}
